import { useState, useRef, useCallback } from 'react';
import LocationPrediction from '../Models/LocationPrediction';
import LocationDetails from '../Models/LocationDetails';
import convertGoogleAutocompletePredictionsToLocationPredictions from '../Utils/convertGoogleAutocompletePredictionsToLocationPredictions';
import convertGooglePlaceToLocationDetails from '../Utils/convertGooglePlaceToLocationDetails';
import LocationConsts from '../Utils/LocationConsts';

let autocompleteService: google.maps.places.AutocompleteService | undefined;
let placesService: google.maps.places.PlacesService | undefined;
let sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

function ensureAutocompleteService() {
    if (!autocompleteService && (window as any).google) {
        autocompleteService = new google.maps.places.AutocompleteService();
    }
    return autocompleteService;
}

function ensurePlacesService() {
    if (!placesService && (window as any).google) {
        placesService = new google.maps.places.PlacesService(document.createElement('div'));
    }
    return placesService;
}

function ensureSessionToken() {
    if (!sessionToken && (window as any).google) {
        sessionToken = new google.maps.places.AutocompleteSessionToken();
    }
    return sessionToken;
}

interface UseGooglePlacesAutocompleteReturn {
    options: LocationPrediction[];
    loading: boolean;
    fetchPredictions: (input: string, locationType: string) => void;
    selectPrediction: (
        prediction: LocationPrediction,
        locationType: string,
        callback: (details: LocationDetails | null) => void,
    ) => void;
}

function useGooglePlacesAutocomplete(): UseGooglePlacesAutocompleteReturn {
    const [options, setOptions] = useState<LocationPrediction[]>([]);
    const [loading, setLoading] = useState(false);
    const activeRef = useRef(true);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchPredictions = useCallback((input: string, locationType: string) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (!input) {
            setOptions([]);
            setLoading(false);
            return;
        }

        debounceRef.current = setTimeout(() => {
            const service = ensureAutocompleteService();
            const token = ensureSessionToken();
            if (!service || !token) return;

            setLoading(true);

            const request = {
                input,
                types: [`(${locationType})`],
                language: LocationConsts.Language,
                sessionToken: token,
            };

            service.getPlacePredictions(request, (results?: google.maps.places.AutocompletePrediction[]) => {
                if (activeRef.current && results) {
                    setOptions(
                        convertGoogleAutocompletePredictionsToLocationPredictions(locationType === 'regions', results),
                    );
                }
                setLoading(false);
            });
        }, 200);
    }, []);

    const selectPrediction = useCallback(
        (prediction: LocationPrediction, locationType: string, callback: (details: LocationDetails | null) => void) => {
            const service = ensurePlacesService();
            if (!service) return;

            const request = {
                placeId: prediction.locationId,
                types: [`(${locationType})`],
                fields: ['place_id', 'address_components', 'geometry.location', 'utc_offset_minutes'],
                language: LocationConsts.Language,
                sessionToken: ensureSessionToken(),
            };

            placesService?.getDetails(request, (place, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                    const details = convertGooglePlaceToLocationDetails(place, locationType === 'regions');
                    callback(details);
                } else {
                    callback(null);
                }
            });

            sessionToken = new google.maps.places.AutocompleteSessionToken();
        },
        [],
    );

    return { options, loading, fetchPredictions, selectPrediction };
}

export default useGooglePlacesAutocomplete;
